import React from 'react';
import { cx } from 'linaria';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Typography, Layout } from 'fronton-react';
import { patchUrlByLocale } from 'utils';
import { useBreakpoints } from 'common/hooks/use-breakpoints';
import { getStoreFeatures } from 'features/store/ducks/get-store-by-id';
import { getTitle } from 'features/page-model/ducks/get-model';
import { CarouselItem } from 'common/components/carousel-components/carousel-item';
import { Carousel } from 'common/components/carousel-components/carousel';
import { heading } from 'features/store-projects/styles';
import { analyticsUtils } from 'utils/analytics-utils';
import { textDecorationNone } from 'common/styles';
import { getStorePathname } from 'common/selectors/location';
import {
  TopLayoutContainer,
  active,
  itemStyles,
  promoCarouselWrapperStyles,
} from './service-carousel.styles';
import { ServiceCard } from '../service-card';
import { useHasServicesLinks } from 'common/hooks/use-has-services-links';

export const ServiceCarousel = () => {
  const { t } = useTranslation();
  const itemsData = useSelector(getStoreFeatures) || [];
  const { smMobile, mobile } = useBreakpoints() ?? {};
  const title = useSelector(getTitle) || '';
  const storePathname = useSelector(getStorePathname);
  const servicesInStore = t('servicesInStore');

  const hasLink = useHasServicesLinks();
  const Tag = hasLink ? 'a' : 'span';

  if (!itemsData?.length) {
    return null;
  }

  const sendClickGA = (name?: string) => {
    analyticsUtils.sendEvent('GA:clickButton', {
      elementType: 'button',
      elementBlock: 'shopPage', // блок, в котором расположен элемент
      elementName: `${title} / ${servicesInStore}`, // название блока
      elementText: name, // заголовок карточки (switch-forward или switch-backward для стрелочек)
    });
  };

  return (
    <TopLayoutContainer data-qa="service-carousel">
      <Layout>
        <Typography variant="h3" className={heading}>
          {servicesInStore}
        </Typography>
      </Layout>
      <div className={promoCarouselWrapperStyles}>
        {!(smMobile || mobile) && (
          <Carousel
            controlSettings={{
              variant: 'outline',
              size: 'l',
              rounded: true,
              iconOnly: true,
              iconSize: 16,
            }}
          >
            {itemsData.map(({ name, imgUrl, pagePath, description }, idx: number) => {
              const href = hasLink ? patchUrlByLocale(`${pagePath}${storePathname}/`) : undefined;
              const className = cx(itemStyles, hasLink ? active : null);

              return (
                <CarouselItem
                  data={{
                    idx,
                    elementBlock: title,
                    text: name,
                  }}
                  key={`CarouselItem-${pagePath}`}
                  as={hasLink ? 'a' : 'span'}
                  href={href}
                  className={className}
                  onSendAnalytics={sendClickGA}
                  variant="secondary"
                >
                  <ServiceCard title={name} text={description} image={imgUrl} size="sm" />
                </CarouselItem>
              );
            })}
          </Carousel>
        )}
        {(smMobile || mobile) &&
          itemsData.map(({ name, imgUrl, pagePath, description }) => {
            const href = patchUrlByLocale(`${pagePath}${storePathname}/`);
            return (
              <Tag
                key={pagePath}
                className={textDecorationNone}
                rel="noopener"
                target="_blank"
                href={hasLink ? href : undefined}
                onClick={() => (hasLink ? sendClickGA(name) : undefined)}
              >
                <ServiceCard key={pagePath} title={name} text={description} image={imgUrl} size="sm" />
              </Tag>
            );
          })}
      </div>
    </TopLayoutContainer>
  );
};
